import { addDays } from "../gulaTasks";
import {
  fullName,
  type GulaClient,
  type GulaOrder,
  type GulaSubscription,
} from "../gulaTypes";
import { currentCallSlot, isInSlot } from "../useAgents";
import { clientName, isDue, type WelcomeCall } from "../welcome/welcome";

export type AlertTone = "critical" | "warning" | "info";

export interface HomeAlert {
  key: string;
  tone: AlertTone;
  title: string;
  link: string;
  items: { label: string; link: string }[];
}

const HOUR = 3600 * 1000;
const olderThan = (date: string | null | undefined, hours: number, now: Date) =>
  !!date && now.getTime() - new Date(date).getTime() > hours * HOUR;

const startOfToday = (now: Date) => {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  return today;
};

const clientLink = (clientId: unknown) => `/clientes?cliente=${clientId}`;
const ACTIVE_LEAD_STAGES = [
  "new",
  "no_answer",
  "first_contact",
  "second_contact",
];

/**
 * Everything that is late or waiting for someone, most urgent first.
 * Pure: works on the data already loaded by Inicio.
 */
/** Alerts about orders and payments: for the people who run shipping and money. */
const OPERATIONS_ALERTS = new Set([
  "unpaid",
  "received-not-notified",
  "to-ship-late",
  "shipped-late",
  "no-install-date",
  "install-unconfirmed",
]);

/** What a setter sees: only the alerts about leads and calls. */
export const salesAlertsOnly = (alerts: HomeAlert[]) =>
  alerts.filter((alert) => !OPERATIONS_ALERTS.has(alert.key));

export const buildAlerts = ({
  clients,
  orders,
  subscriptions,
  welcomeCalls = [],
  now = new Date(),
}: {
  clients: GulaClient[];
  orders: GulaOrder[];
  subscriptions: GulaSubscription[];
  /** Pending welcome calls (gula_welcome_calls_summary) */
  welcomeCalls?: WelcomeCall[];
  now?: Date;
}): HomeAlert[] => {
  const today = startOfToday(now);
  const slot = currentCallSlot(now);
  const clientItems = (list: GulaClient[]) =>
    list.map((client) => ({
      label: fullName(client),
      link: clientLink(client.id),
    }));
  const orderItems = (list: GulaOrder[]) =>
    list.map((order) => ({
      label: `${fullName(order)}${order.city ? ` (${order.city})` : ""}`,
      link: "/operaciones",
    }));

  const alerts: HomeAlert[] = [
    {
      key: "stale-leads",
      tone: "critical",
      title: "Leads de más de 24 h sin primera llamada",
      link: "/clientes",
      items: clientItems(
        clients.filter(
          (client) =>
            client.stage === "new" && olderThan(client.created_at, 24, now),
        ),
      ),
    },
    {
      key: "recontact-overdue",
      tone: "critical",
      title: "Recontactos con la fecha pasada",
      link: "/clientes",
      items: clientItems(
        clients.filter(
          (client) =>
            client.stage === "recontact" &&
            !!client.recontact_at &&
            new Date(client.recontact_at) < today,
        ),
      ),
    },
    {
      key: "unpaid",
      tone: "critical",
      title: "Mantenimientos o alquileres impagados",
      link: "/clientes",
      items: subscriptions
        .filter((subscription) =>
          ["past_due", "unpaid"].includes(subscription.status),
        )
        .map((subscription) => {
          const client = clients.find(
            (item) =>
              String(item.contact_id) === String(subscription.contact_id),
          );
          return {
            label: client
              ? fullName(client)
              : (subscription.plan_name ?? "Suscripción"),
            link: client ? clientLink(client.id) : "/clientes",
          };
        }),
    },
    {
      key: "received-not-notified",
      tone: "critical",
      title: "Recibido hace más de 1 día y sin avisar al instalador",
      link: "/operaciones",
      items: orderItems(
        orders.filter(
          (order) =>
            order.stage === "received" &&
            olderThan(order.stage_changed_at, 24, now),
        ),
      ),
    },
    {
      key: "to-ship-late",
      tone: "warning",
      title: "Pedidos sin enviar desde hace más de 2 días",
      link: "/operaciones",
      items: orderItems(
        orders.filter(
          (order) =>
            order.stage === "to_ship" &&
            olderThan(order.stage_changed_at, 48, now),
        ),
      ),
    },
    {
      key: "shipped-late",
      tone: "warning",
      title: "Enviados hace más de 5 días y sin marcar como recibidos",
      link: "/operaciones",
      items: orderItems(
        orders.filter(
          (order) =>
            order.stage === "shipped" &&
            olderThan(order.stage_changed_at, 120, now),
        ),
      ),
    },
    {
      key: "no-install-date",
      tone: "warning",
      title: "Instalación solicitada hace más de 3 días y sin fecha",
      link: "/operaciones",
      items: orderItems(
        orders.filter(
          (order) =>
            order.stage === "installer_notified" &&
            !order.installation_date &&
            olderThan(order.stage_changed_at, 72, now),
        ),
      ),
    },
    {
      key: "install-unconfirmed",
      tone: "warning",
      title: "Fecha de instalación pasada y sin marcar como instalado",
      link: "/operaciones",
      items: orderItems(
        orders.filter(
          (order) =>
            order.stage === "installer_notified" &&
            !!order.installation_date &&
            new Date(order.installation_date) < today,
        ),
      ),
    },
    {
      key: "welcome-due",
      tone: "warning",
      title: "Llamadas de bienvenida para hoy (recién instalados)",
      link: "/mi-jornada",
      items: welcomeCalls
        .filter((call) => call.status === "pending" && isDue(call, now))
        .map((call) => ({
          label: `${clientName(call)}${call.city ? ` (${call.city})` : ""}`,
          link: call.client_id ? clientLink(call.client_id) : "/mi-jornada",
        })),
    },
    {
      key: "unassigned",
      tone: "warning",
      title: "Leads nuevos sin coger",
      link: "/clientes?agente=none",
      items: clientItems(
        clients.filter(
          (client) => client.stage === "new" && client.sales_id == null,
        ),
      ),
    },
    {
      key: "call-now",
      tone: "info",
      title: "Quieren que les llamen ahora (su franja)",
      link: "/clientes?franja=now",
      items: slot
        ? clientItems(
            clients.filter(
              (client) =>
                ACTIVE_LEAD_STAGES.includes(client.stage) &&
                isInSlot(client.call_slot, slot),
            ),
          )
        : [],
    },
  ];
  return alerts.filter((alert) => alert.items.length > 0);
};

/** Installations planned in the next days. */
export const upcomingInstallations = (
  orders: GulaOrder[],
  days: number,
  now = new Date(),
) => {
  const today = startOfToday(now);
  const limit = addDays(today, days);
  return orders.filter(
    (order) =>
      order.stage === "installer_notified" &&
      !!order.installation_date &&
      new Date(order.installation_date) >= today &&
      new Date(order.installation_date) < limit,
  );
};
